import {
  createSlice,
  createEntityAdapter,
  createAsyncThunk,
  EntityState,
  createSelector,
} from '@reduxjs/toolkit';
import { RootState, ThunkApi } from 'state/store';
import { selectAllLDUsers } from 'state/UsersManagement/usersManagementSlice';

import {
  LearningTeam as BaseLearningTeam,
  LearningTeamWithMembers,
} from 'utils/types/learningTeam';
import { SLICE_STATUS } from 'utils/constants';
import {
  generateSuccessNotification,
  generateErrorNotification,
  serializeLearningTeam,
  generateUpdateLDTeamMembersNotifications,
  generateRemoveTeamMemberNotification,
} from './utils';
import { isUserStatusDisabled } from 'utils/functions';
import { BasicInfoForm } from 'types/store/learningTeam';
import { SnackBarNotificationParams } from 'Hooks/useSnackbarNotification';
import learningTeamAPI from '../LearningTeams/learningTeamsAPI';
import { User, Status, LDUser, FormOption } from 'utils/customTypes';
import type { LearningTeamSummary } from 'types/store/normalized';

const usersAdapter = createEntityAdapter<User>();

export interface LearningTeam extends BaseLearningTeam {
  membersIds: string[];
  managersIds: string[];
}

interface LearningTeamState {
  value?: LearningTeam;
  users: EntityState<User>;
  eligibleParents: LearningTeamSummary[];
  fetchLearningTeamStatus?: Status;
  fetchEligibleParentsStatus?: Status;
  updateLearningTeamStatus?: Status;
  updateLDTeamMembersStatus?: Status;
  updateLearningTeamNotification?: SnackBarNotificationParams;
  updateLDTeamMembersNotifications?: SnackBarNotificationParams[];
}

const initialState: LearningTeamState = {
  value: undefined,
  users: usersAdapter.getInitialState(),
  eligibleParents: [],
  fetchLearningTeamStatus: undefined,
  updateLearningTeamStatus: undefined,
};

interface FetchLearningTeamParams {
  id: string;
}

export const fetchLearningTeam = createAsyncThunk<
  LearningTeamWithMembers,
  FetchLearningTeamParams,
  ThunkApi
>('learningTeam/fetchLearningTeam', async ({ id }, { rejectWithValue }) => {
  try {
    const response = await learningTeamAPI.getLearningTeam(id);
    return response.data.learningTeam;
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const fetchEligibleParentsForTeam = createAsyncThunk<
  LearningTeamSummary[],
  string,
  ThunkApi
>(
  'learningTeam/fetchEligibleParentsForTeam',
  async (teamId, { extra: { getLearningTeamsApi }, rejectWithValue }) => {
    try {
      return await getLearningTeamsApi().getEligibleParents(teamId);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

interface UpdateLearningTeamParams {
  id: string;
  updateFields: Partial<BasicInfoForm>;
}

export const updateLearningTeam = createAsyncThunk<
  { learningTeam: LearningTeamWithMembers; teamManagers?: string[] },
  UpdateLearningTeamParams,
  ThunkApi
>(
  'learningTeam/updateLearningTeam',
  async ({ id, updateFields }, { rejectWithValue, extra }) => {
    try {
      const { teamManagers, ...legacyUpdateFields } = updateFields;
      // Call legacy API first so a failure there leaves managers unchanged
      const response = await learningTeamAPI.updateTeam(id, legacyUpdateFields);
      if (teamManagers !== undefined) {
        await extra.getLearningTeamsApi().setManagers(id, teamManagers);
      }
      return { learningTeam: response.data.learningTeam, teamManagers };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

interface UpdateUserInLdTeamParams {
  userId: string;
  teamId: string;
}

export const addUserToLdTeam = createAsyncThunk<
  LearningTeamWithMembers,
  UpdateUserInLdTeamParams,
  ThunkApi
>(
  'learningTeam/addUserToLdTeam',
  async ({ userId, teamId }, { rejectWithValue }) => {
    try {
      const response = await learningTeamAPI.addUserToLdTeam(userId, teamId);
      return response.data.learningTeam;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const removeUserFromLdTeam = createAsyncThunk<
  LearningTeamWithMembers,
  UpdateUserInLdTeamParams,
  ThunkApi
>(
  'learningTeam/removeUserFromLdTeam',
  async ({ userId, teamId }, { rejectWithValue }) => {
    try {
      const response = await learningTeamAPI.removeUserFromLdTeam(
        userId,
        teamId
      );
      return response.data.learningTeam;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

const learningTeamSlice = createSlice({
  name: 'learningTeam',
  initialState,
  reducers: {
    resetEligibleParents: (state) => {
      state.eligibleParents = [];
      state.fetchEligibleParentsStatus = undefined;
    },
    clearUpdateLearningTeamNotification: (state) => {
      state.updateLearningTeamNotification = undefined;
    },
    setUpdateLDTeamMembersNotifications: (
      state,
      action: {
        payload: {
          successCount: number;
          errorCount: number;
        };
      }
    ) => {
      state.updateLDTeamMembersNotifications =
        generateUpdateLDTeamMembersNotifications(
          action.payload.successCount,
          action.payload.errorCount
        );
    },
    clearUpdateLDTeamMembersNotifications: (state) => {
      state.updateLDTeamMembersNotifications = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEligibleParentsForTeam.pending, (state) => {
        state.fetchEligibleParentsStatus = SLICE_STATUS.LOADING;
      })
      .addCase(fetchEligibleParentsForTeam.rejected, (state) => {
        state.fetchEligibleParentsStatus = SLICE_STATUS.FAILED;
      })
      .addCase(fetchEligibleParentsForTeam.fulfilled, (state, action) => {
        state.eligibleParents = action.payload;
        state.fetchEligibleParentsStatus = SLICE_STATUS.IDLE;
      })
      .addCase(fetchLearningTeam.pending, (state) => {
        state.fetchLearningTeamStatus = SLICE_STATUS.LOADING;
      })
      .addCase(fetchLearningTeam.rejected, (state) => {
        state.fetchLearningTeamStatus = SLICE_STATUS.FAILED;
      })
      .addCase(fetchLearningTeam.fulfilled, (state, action) => {
        const { learningTeam, entities } = serializeLearningTeam(
          action.payload
        );
        state.value = learningTeam;
        usersAdapter.upsertMany(state.users, Object.fromEntries(entities));
        state.fetchLearningTeamStatus = SLICE_STATUS.IDLE;
      })
      .addCase(updateLearningTeam.pending, (state) => {
        state.updateLearningTeamStatus = SLICE_STATUS.LOADING;
      })
      .addCase(updateLearningTeam.rejected, (state) => {
        state.updateLearningTeamStatus = SLICE_STATUS.FAILED;
        const errorNotification = generateErrorNotification();
        state.updateLearningTeamNotification = errorNotification;
      })
      .addCase(updateLearningTeam.fulfilled, (state, action) => {
        const { learningTeam: learningTeamData, teamManagers } = action.payload;
        const { learningTeam, entities } =
          serializeLearningTeam(learningTeamData);
        state.value = {
          ...state.value,
          ...learningTeam,
          // Override managersIds with what was sent to cognota-be rather than
          // relying on the legacy API response, which is unaware of manager changes
          ...(teamManagers !== undefined ? { managersIds: teamManagers } : {}),
        };
        state.users = usersAdapter.setAll(
          usersAdapter.getInitialState(),
          Object.fromEntries(entities)
        );
        const successNotification = generateSuccessNotification();
        state.updateLearningTeamNotification = successNotification;
        state.updateLearningTeamStatus = SLICE_STATUS.IDLE;
      })
      .addCase(addUserToLdTeam.pending, (state) => {
        state.updateLDTeamMembersStatus = SLICE_STATUS.LOADING;
      })
      .addCase(addUserToLdTeam.fulfilled, (state, action) => {
        const { learningTeam, entities } = serializeLearningTeam(
          action.payload
        );
        const updatedLDTeam = { ...state.value } as LearningTeam;
        updatedLDTeam.membersIds = learningTeam.membersIds;
        state.value = updatedLDTeam;
        state.users = usersAdapter.setAll(
          usersAdapter.getInitialState(),
          Object.fromEntries(entities)
        );
        state.updateLDTeamMembersStatus = SLICE_STATUS.IDLE;
      })
      .addCase(addUserToLdTeam.rejected, (state, action) => {
        state.updateLDTeamMembersStatus = SLICE_STATUS.FAILED;
      })
      .addCase(removeUserFromLdTeam.fulfilled, (state, action) => {
        const { learningTeam, entities } = serializeLearningTeam(
          action.payload
        );
        const updatedLDTeam = { ...state.value } as LearningTeam;
        updatedLDTeam.membersIds = learningTeam.membersIds;
        state.value = updatedLDTeam;
        state.users = usersAdapter.setAll(
          usersAdapter.getInitialState(),
          Object.fromEntries(entities)
        );
        const successNotification = generateRemoveTeamMemberNotification(true);
        state.updateLDTeamMembersNotifications = [successNotification];
      })
      .addCase(removeUserFromLdTeam.rejected, (state, action) => {
        const errorNotification = generateRemoveTeamMemberNotification(false);
        state.updateLDTeamMembersNotifications = [errorNotification];
      });
  },
});

export const {
  selectIds: selectLearningTeamMembersIds,
  selectById: selectLearningTeamMemberById,
  selectAll: selectAllLearningTeamMembers,
} = usersAdapter.getSelectors((state: RootState) => state.learningTeam.users);

export const selectLearningTeam = (state: RootState) =>
  state.learningTeam.value;

export const selectEligibleParentsForTeam = (state: RootState) =>
  state.learningTeam.eligibleParents;

export const selectEligibleParentsForDropdown = createSelector(
  [selectEligibleParentsForTeam],
  (eligibleParents): FormOption[] =>
    eligibleParents.map((team) => ({ label: team.name, value: team.id }))
);

export const selectIsEligibleParentsLoading = (state: RootState) =>
  state.learningTeam.fetchEligibleParentsStatus === SLICE_STATUS.LOADING;

export const selectLearningTeamName = createSelector(
  [selectLearningTeam],
  (learningTeam: LearningTeam | undefined) => learningTeam?.name || ''
);

export const selectIsLearningTeamLoading = (state: RootState) =>
  state.learningTeam.fetchLearningTeamStatus === SLICE_STATUS.LOADING;

export const selectIsUpdatingLearningTeam = (state: RootState) =>
  state.learningTeam.updateLearningTeamStatus === SLICE_STATUS.LOADING;

export const selectIsUpdatingLDTeamMembers = (state: RootState) =>
  state.learningTeam.updateLDTeamMembersStatus === SLICE_STATUS.LOADING;

export const selectNonTeamMembers = createSelector(
  [selectAllLDUsers, selectLearningTeamMembersIds],
  (ldUsers: LDUser[], learningTeamMembersIds: (string | number)[]) => {
    // Ensure all IDs are strings for comparison
    const stringIds = learningTeamMembersIds.map(String);
    return ldUsers.filter((user) => !stringIds.includes(String(user.id)));
  }
);

export const selectTeamManagers = createSelector(
  [selectLearningTeam, (state: RootState) => state],
  (learningTeam: LearningTeam | undefined, state: RootState) => {
    if (!learningTeam) return [];
    return learningTeam.managersIds
      .map((managerId) => selectLearningTeamMemberById(state, managerId))
      .filter((manager) => manager !== undefined);
  }
);

export const selectAvailableLDUsersToAdd = createSelector(
  [selectAllLDUsers, selectLearningTeam],
  (ldUsers: LDUser[], learningTeam: LearningTeam | undefined) => {
    return ldUsers
      .filter((user) => !learningTeam?.membersIds.includes(user.id))
      .filter((user) => !isUserStatusDisabled(user.status))
      .map((user) => ({
        ...user,
        isTeamManager: learningTeam?.managersIds?.includes(user.id),
      }));
  }
);

export const selectLearningTeamMembers = createSelector(
  [selectLearningTeam, selectAllLearningTeamMembers],
  (
    learningTeam: LearningTeam | undefined,
    learningTeamMembersEntities: User[]
  ) => {
    return learningTeamMembersEntities.filter((user) =>
      learningTeam?.membersIds.includes(user.id)
    ) as LDUser[];
  }
);

export const selectUpdateLearningTeamNotification = (state: RootState) =>
  state.learningTeam.updateLearningTeamNotification;

export const selectUpdateLDTeamMembersNotifications = (state: RootState) =>
  state.learningTeam.updateLDTeamMembersNotifications;

export const {
  resetEligibleParents,
  clearUpdateLearningTeamNotification,
  setUpdateLDTeamMembersNotifications,
  clearUpdateLDTeamMembersNotifications,
} = learningTeamSlice.actions;

export default learningTeamSlice.reducer;
